import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { KpiTile as SharedKpiTile } from '@/components/uren';
import { LoonbasisWizard } from '@/components/LoonbasisWizard';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  Euro,
  Loader2,
  RefreshCw,
  Lock,
  Unlock,
  Download,
  AlertCircle,
  Pencil,
  Save,
  X,
  Plus,
  Banknote,
  Briefcase,
  Receipt,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useLocationUrlSync } from '@/contexts/LocationContext';
import { formatHoursHM, getISOWeek, navigateIsoWeek } from '@/lib/format';

const fmtEur = (v) => {
  if (v === null || v === undefined) return '—';
  return `€ ${Number(v).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

function KpiTile({ icon: Icon, label, value, sublabel, accent }) {
  // Local wrapper around shared KpiTile to preserve legacy data-testid casing
  // (replace [^a-z]+ matches the shared component's behaviour).
  return (
    <SharedKpiTile icon={Icon} label={label} value={value} sublabel={sublabel} accent={accent} />
  );
}

function LoonControleCard() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [wizard, setWizard] = useState(false);

  const reload = () => api.get('/employees/loon-controle')
    .then((r) => setData(r.data))
    .catch(() => setData(null));

  useEffect(() => { reload(); }, []);

  if (!data || (data.zonder_loonbasis + data.wacht_op_besluit) === 0) return null;

  const groups = [
    ['oproep_zonder_uurloon', 'Oproep zonder uurloon'],
    ['vast_zonder_loonbasis', 'Vast/tijdelijk zonder loonbasis'],
    ['alleen_maandsalaris', 'Alleen maandsalaris — wacht op uurloon-besluit'],
  ];

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm border-l-[3px] border-l-amber-400"
      data-testid="loon-controle-card"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="loon-controle-toggle"
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <AlertCircle size={16} className="text-amber-600" /> Loon-controle
        </span>
        <span className="text-sm text-slate-700" data-testid="loon-controle-summary">
          <span className="font-semibold text-amber-700">{data.zonder_loonbasis} zonder loonbasis</span>
          <span className="text-slate-400"> · </span>
          <span className="text-slate-600">{data.wacht_op_besluit} wachten op uurloon-besluit</span>
        </span>
      </button>
      {data.aangevuld_7d > 0 && (
        <p className="mt-1.5 text-xs text-emerald-700" data-testid="loon-controle-aangevuld-7d">
          ✓ afgelopen 7 dagen aangevuld: {data.aangevuld_7d}
        </p>
      )}
      {data.zonder_loonbasis > 0 && (
        <button
          type="button"
          onClick={() => setWizard(true)}
          data-testid="btn-loonbasis-wizard"
          className="mt-2 rounded-md border border-slate-300 px-3 py-1.5 text-xs hover:bg-slate-50"
        >
          Lijst langslopen
        </button>
      )}
      {wizard && (
        <LoonbasisWizard
          groups={data.groups}
          onSaved={reload}
          onClose={() => { setWizard(false); reload(); }}
        />
      )}
      {open && (
        <div className="mt-3 space-y-3" data-testid="loon-controle-lijst">
          {groups.map(([key, label]) => {
            const rows = data.groups?.[key] || [];
            if (rows.length === 0) return null;
            return (
              <div key={key} data-testid={`loon-groep-${key}`}>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {label} ({rows.length})
                </p>
                <div className="mt-1 divide-y divide-slate-100">
                  {rows.map((r) => (
                    <a
                      key={r.id}
                      href={`/medewerkers/${r.id}`}
                      data-testid={`loon-rij-${r.personeelsnummer}`}
                      className="flex flex-wrap items-baseline justify-between gap-2 py-1.5 text-xs hover:bg-slate-50"
                    >
                      <span className="text-slate-800">
                        {r.name} <span className="text-slate-400">#{r.personeelsnummer}</span>
                      </span>
                      <span className="text-slate-500">
                        {r.location_name} · {r.contract_type} · {r.mist}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            );
          })}
          <p className="text-[11px] text-slate-500">
            De verloning rekent met het uurloon-veld. Er wordt niets automatisch afgeleid.
          </p>
        </div>
      )}
    </div>
  );
}


export default function Verloning() {
  const { toast } = useToast();
  const { user } = useAuth();
  const { slug: selectedSlug, setSlug: setSelectedSlug } = useLocationUrlSync();
  const isOwnerAdmin = user && (user.role === 'owner' || user.role === 'admin');

  const [locations, setLocations] = useState([]);
  const initial = getISOWeek();
  const [year, setYear] = useState(initial.year);
  const [week, setWeek] = useState(initial.week);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);
  const [confirmLock, setConfirmLock] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  // Locations bootstrap
  useEffect(() => {
    (async () => {
      try {
        const { data: locs } = await api.get('/locations');
        const eligible = (locs || [])
          .filter((l) => l.has_roster !== false)
          .sort((a, b) => a.name.localeCompare(b.name, 'nl'));
        setLocations(eligible);
      } catch {
        toast({ title: 'Kon locaties niet laden', variant: 'destructive' });
      }
    })();
  }, [toast]);

  // Fallback selection (URL-aware via context).
  useEffect(() => {
    if (locations.length > 0 && !selectedSlug) {
      setSelectedSlug(locations[0].slug);
    }
  }, [locations, selectedSlug, setSelectedSlug]);

  const loadWeek = useCallback(async () => {
    if (!selectedSlug) return;
    setLoading(true);
    try {
      const { data: resp } = await api.get('/payroll/week', {
        params: { location_id: selectedSlug, year, week },
      });
      setData(resp);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Kon verloning niet laden';
      toast({ title: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [selectedSlug, year, week, toast]);

  useEffect(() => { loadWeek(); }, [loadWeek]);

  const shiftWeek = (delta) => {
    const { year: y, week: w } = navigateIsoWeek(year, week, delta);
    setWeek(w);
    setYear(y);
  };

  const periodLocked = data?.period?.status === 'locked' || data?.period?.status === 'exported';

  const openEditRow = (row) => {
    setEditRow(row);
    setEditForm({
      overwerk_uren: row.overwerk_uren ?? '',
      overwerk_eur: '', // optional override; default leeg → uurloon × uren
      reiskosten_eur: row.reiskosten_eur ?? '',
      bonus_eur: row.bonus_eur ?? '',
      notitie: row.notitie ?? '',
    });
  };

  const closeEditRow = () => {
    setEditRow(null);
    setEditForm({});
  };

  const submitAdjustment = async () => {
    if (!editRow) return;
    setEditSaving(true);
    try {
      const payload = {
        location_id: selectedSlug,
        employee_id: editRow.employee_id,
        year,
        week_number: week,
        overwerk_uren: editForm.overwerk_uren === '' ? null : parseFloat(editForm.overwerk_uren),
        overwerk_eur: editForm.overwerk_eur === '' ? null : parseFloat(editForm.overwerk_eur),
        reiskosten_eur: editForm.reiskosten_eur === '' ? null : parseFloat(editForm.reiskosten_eur),
        bonus_eur: editForm.bonus_eur === '' ? null : parseFloat(editForm.bonus_eur),
        notitie: editForm.notitie || null,
      };
      await api.put('/payroll/adjustments', payload);
      toast({ title: 'Bijgewerkt', description: `${editRow.name} — week ${week}/${year}` });
      closeEditRow();
      await loadWeek();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let msg = typeof detail === 'string' ? detail : 'Opslaan mislukt';
      if (status === 423) msg = detail || 'Periode is afgesloten — ontgrendel eerst';
      if (status === 422 && Array.isArray(detail)) msg = `Validatiefout: ${detail.map((d) => d.msg || d.type).slice(0, 2).join(', ')}`;
      toast({ title: msg, variant: 'destructive' });
    } finally {
      setEditSaving(false);
    }
  };

  const doExport = async () => {
    setExportLoading(true);
    try {
      const resp = await api.get('/payroll/export-csv', {
        params: { location_id: selectedSlug, year, week },
        responseType: 'blob',
      });
      const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `verloning_${selectedSlug}_${year}_W${String(week).padStart(2, '0')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'CSV gedownload', description: `${selectedSlug} W${week}/${year}` });
      await loadWeek();
    } catch (e) {
      toast({ title: 'Export mislukt', variant: 'destructive' });
    } finally {
      setExportLoading(false);
    }
  };

  const doLock = async () => {
    setConfirmLock(false);
    setLockBusy(true);
    try {
      await api.post('/payroll/lock', { location_id: selectedSlug, year, week });
      toast({ title: 'Periode afgesloten', description: `Week ${week}/${year} is nu definitief` });
      await loadWeek();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Lock mislukt';
      toast({ title: detail, variant: 'destructive' });
    } finally {
      setLockBusy(false);
    }
  };

  const doUnlock = async () => {
    setLockBusy(true);
    try {
      await api.post('/payroll/unlock', { location_id: selectedSlug, year, week });
      toast({ title: 'Periode ontgrendeld', description: `Week ${week}/${year} is weer bewerkbaar` });
      await loadWeek();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Unlock mislukt';
      toast({ title: detail, variant: 'destructive' });
    } finally {
      setLockBusy(false);
    }
  };

  const totals = data?.totals || {};
  const rows = data?.per_employee || [];

  return (
    <div className="space-y-5" data-testid="page-verloning">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Verloning</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            Bruto loon per medewerker (basis = goedgekeurde uren × uurloon). Overwerk, reiskosten en bonus zijn handmatig.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            data-testid="verloning-location-select"
            value={selectedSlug}
            onChange={(e) => setSelectedSlug(e.target.value)}
            disabled={loading}
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm shadow-sm focus:border-brand-amber focus:outline-none disabled:opacity-50"
          >
            {locations.map((loc) => (
              <option key={loc.slug} value={loc.slug}>{loc.name}</option>
            ))}
          </select>
          <div className="inline-flex items-center rounded-md border border-slate-200 bg-white">
            <button
              type="button"
              data-testid="verloning-week-prev"
              onClick={() => shiftWeek(-1)}
              className="flex h-9 w-9 items-center justify-center rounded-l-md text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              aria-label="Vorige week"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="px-3 py-1.5 text-sm font-medium text-slate-700" data-testid="verloning-week-label">
              Week {week} / {year}
            </div>
            <button
              type="button"
              data-testid="verloning-week-next"
              onClick={() => shiftWeek(1)}
              className="flex h-9 w-9 items-center justify-center rounded-r-md text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              aria-label="Volgende week"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <Button type="button" variant="outline" size="sm" data-testid="verloning-refresh" onClick={loadWeek} disabled={loading} className="h-9">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            <span className="ml-1.5">Verversen</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="verloning-export"
            onClick={doExport}
            disabled={exportLoading || loading || !rows.length}
            className="h-9"
          >
            {exportLoading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            <span className="ml-1.5">Export CSV</span>
          </Button>
          {!periodLocked ? (
            <Button
              type="button"
              size="sm"
              data-testid="verloning-lock-btn"
              onClick={() => setConfirmLock(true)}
              disabled={lockBusy || loading || !rows.length}
              className="h-9 bg-brand-navy hover:bg-brand-navy/90 text-white"
            >
              {lockBusy ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
              <span className="ml-1.5">Maak definitief</span>
            </Button>
          ) : isOwnerAdmin ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="verloning-unlock-btn"
              onClick={doUnlock}
              disabled={lockBusy || loading}
              className="h-9 border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              {lockBusy ? <Loader2 size={14} className="animate-spin" /> : <Unlock size={14} />}
              <span className="ml-1.5">Ontgrendel</span>
            </Button>
          ) : (
            <Badge variant="outline" data-testid="verloning-locked-badge" className="h-9 border-emerald-300 bg-emerald-50 px-3 text-emerald-700">
              <Lock size={12} className="mr-1.5" /> Definitief
            </Badge>
          )}
        </div>
      </div>

      {/* Locked banner */}
      {periodLocked && (
        <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="verloning-locked-banner">
          <Lock size={14} />
          <span>
            Deze periode is <strong>{data?.period?.status === 'exported' ? 'geëxporteerd' : 'definitief gemaakt'}</strong> op{' '}
            {data?.period?.locked_at && new Date(data.period.locked_at).toLocaleDateString('nl-NL')}.
            Wijzigingen zijn geblokkeerd (alleen owner/admin kan ontgrendelen).
          </span>
        </div>
      )}

      {/* KPIs */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <KpiTile icon={Briefcase} label="Medewerkers" value={totals.employees || 0} sublabel="met activiteit deze week" accent="bg-brand-navy/10 text-brand-navy" />
        <KpiTile icon={Clock} label="Gewerkte uren" value={formatHoursHM(totals.hours_total || 0)} sublabel="alleen goedgekeurd" />
        <KpiTile icon={Banknote} label="Basis loon" value={fmtEur(totals.basis_eur || 0)} sublabel="uren × uurloon" accent="bg-emerald-100 text-emerald-700" />
        <KpiTile icon={Receipt} label="Reiskosten" value={fmtEur(totals.reiskosten_eur || 0)} sublabel="netto, onbelast" accent="bg-slate-100 text-slate-700" />
        <KpiTile icon={Euro} label="Totaal uitbetaling" value={fmtEur(totals.total_payout_eur || 0)} sublabel="bruto + reiskosten" accent="bg-brand-amber/15 text-brand-amber-dark" />
      </div>

      {/* Loon-controle */}
      <LoonControleCard />

      {/* Table */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="verloning-table">
        {loading ? (
          <div className="p-10 text-center text-slate-500 flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Laden…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <Calendar size={32} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-500">Geen activiteit voor deze week.</p>
            <p className="mt-1 text-xs text-slate-400">Plan eerst shifts en keur uren goed via Uren Overzicht.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-[240px]">Medewerker</TableHead>
                <TableHead className="text-right">Uurloon</TableHead>
                <TableHead className="text-right">Goedgekeurd</TableHead>
                <TableHead className="text-right">Basis loon</TableHead>
                <TableHead className="text-right">Overwerk</TableHead>
                <TableHead className="text-right">Reiskosten</TableHead>
                <TableHead className="text-right">Bonus</TableHead>
                <TableHead className="text-right">Bruto totaal</TableHead>
                <TableHead className="text-right">Uitbetaling</TableHead>
                <TableHead className="text-center w-[110px]">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.employee_id} data-testid={`verloning-row-${row.personeelsnummer}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-navy/10 text-[10px] font-semibold text-brand-navy">
                        {row.name?.split(' ').slice(0, 2).map((s) => s[0]).join('').toUpperCase() || '?'}
                      </div>
                      <div className="leading-tight">
                        <p className="text-sm font-medium text-slate-900">{row.name}</p>
                        <p className="text-[11px] text-slate-500">
                          #{row.personeelsnummer} · {row.role || 'medewerker'}
                        </p>
                      </div>
                      {row.needs_attention === 'no_uurloon' && (
                        <Badge variant="outline" className="ml-1 border-amber-300 bg-amber-50 text-amber-700 text-[10px]" data-testid={`no-uurloon-${row.personeelsnummer}`}>
                          <AlertCircle size={10} className="mr-0.5" /> uurloon mist
                        </Badge>
                      )}
                      {row.planned_unapproved_count > 0 && (
                        <Badge variant="outline" className="ml-1 border-slate-300 bg-slate-50 text-slate-600 text-[10px]" data-testid={`unapproved-${row.personeelsnummer}`}>
                          {row.planned_unapproved_count} ongoedgekeurd
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums text-slate-600">
                    {row.uurloon ? fmtEur(row.uurloon) : '—'}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    <div className="leading-tight">
                      <p>{formatHoursHM(row.gewerkt_uren)}</p>
                      <p className="text-[10px] text-slate-400">{row.days_worked} dgn</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums font-medium text-slate-900">
                    {fmtEur(row.basis_eur)}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {row.overwerk_eur > 0 || row.overwerk_uren > 0 ? (
                      <div className="leading-tight">
                        <p className="text-amber-700">{fmtEur(row.overwerk_eur)}</p>
                        {row.overwerk_uren > 0 && (
                          <p className="text-[10px] text-slate-400">{formatHoursHM(row.overwerk_uren)}</p>
                        )}
                      </div>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {row.reiskosten_eur > 0 ? <span className="text-slate-700">{fmtEur(row.reiskosten_eur)}</span> : <span className="text-slate-300">—</span>}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {row.bonus_eur ? <span className="text-emerald-700">{fmtEur(row.bonus_eur)}</span> : <span className="text-slate-300">—</span>}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums font-semibold text-slate-900">
                    {fmtEur(row.bruto_loon_eur)}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums font-bold text-brand-navy">
                    {fmtEur(row.totaal_uitbetaling_eur)}
                  </TableCell>
                  <TableCell className="text-center">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`verloning-edit-${row.personeelsnummer}`}
                      onClick={() => openEditRow(row)}
                      disabled={periodLocked}
                      className="h-7 px-2 text-[11px]"
                    >
                      {row.has_adjustment ? <Pencil size={12} className="mr-1" /> : <Plus size={12} className="mr-1" />}
                      {row.has_adjustment ? 'Wijzig' : 'Toevoegen'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Edit adjustment dialog */}
      <Dialog open={!!editRow} onOpenChange={(open) => { if (!open) closeEditRow(); }}>
        <DialogContent data-testid="adjustment-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Verloning aanvulling</DialogTitle>
            <DialogDescription>
              {editRow?.name} · #{editRow?.personeelsnummer} · Week {week}/{year}
            </DialogDescription>
          </DialogHeader>
          {editRow && (
            <div className="space-y-4 py-2">
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                <div className="flex justify-between">
                  <span>Goedgekeurde uren</span>
                  <span className="font-medium tabular-nums">{formatHoursHM(editRow.gewerkt_uren)} ({editRow.days_worked} dgn)</span>
                </div>
                <div className="flex justify-between">
                  <span>Uurloon</span>
                  <span className="font-medium tabular-nums">{editRow.uurloon ? fmtEur(editRow.uurloon) : '— niet ingevuld'}</span>
                </div>
                <div className="flex justify-between border-t border-slate-200 mt-1.5 pt-1.5">
                  <span>Basis loon</span>
                  <span className="font-medium tabular-nums">{fmtEur(editRow.basis_eur)}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="adj-overwerk-uren" className="text-xs">Overwerk (uren)</Label>
                  <Input
                    id="adj-overwerk-uren"
                    data-testid="adj-overwerk-uren"
                    type="number"
                    step="0.25"
                    min="0"
                    placeholder="bv. 2.5"
                    value={editForm.overwerk_uren}
                    onChange={(e) => setEditForm((f) => ({ ...f, overwerk_uren: e.target.value }))}
                    className="tabular-nums"
                  />
                  <p className="text-[10px] text-slate-400">×uurloon = automatisch</p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="adj-overwerk-eur" className="text-xs">Overwerk bedrag (€) — optioneel</Label>
                  <Input
                    id="adj-overwerk-eur"
                    data-testid="adj-overwerk-eur"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="overschrijft bedrag"
                    value={editForm.overwerk_eur}
                    onChange={(e) => setEditForm((f) => ({ ...f, overwerk_eur: e.target.value }))}
                    className="tabular-nums"
                  />
                  <p className="text-[10px] text-slate-400">leeg = uren×uurloon</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="adj-reiskosten" className="text-xs">Reiskosten (€)</Label>
                  <Input
                    id="adj-reiskosten"
                    data-testid="adj-reiskosten"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="bv. 15.50"
                    value={editForm.reiskosten_eur}
                    onChange={(e) => setEditForm((f) => ({ ...f, reiskosten_eur: e.target.value }))}
                    className="tabular-nums"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="adj-bonus" className="text-xs">Bonus / Inhouding (€)</Label>
                  <Input
                    id="adj-bonus"
                    data-testid="adj-bonus"
                    type="number"
                    step="0.01"
                    placeholder="positief = bonus, negatief = inhouding"
                    value={editForm.bonus_eur}
                    onChange={(e) => setEditForm((f) => ({ ...f, bonus_eur: e.target.value }))}
                    className="tabular-nums"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="adj-notitie" className="text-xs">Notitie (intern)</Label>
                <Textarea
                  id="adj-notitie"
                  data-testid="adj-notitie"
                  rows={2}
                  placeholder="Bv. reden voor bonus of overwerk"
                  value={editForm.notitie}
                  onChange={(e) => setEditForm((f) => ({ ...f, notitie: e.target.value }))}
                  maxLength={500}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              data-testid="adjustment-cancel"
              onClick={closeEditRow}
              disabled={editSaving}
            >
              <X size={14} className="mr-1" /> Annuleren
            </Button>
            <Button
              type="button"
              data-testid="adjustment-save"
              onClick={submitAdjustment}
              disabled={editSaving}
              className="bg-brand-navy hover:bg-brand-navy/90 text-white"
            >
              {editSaving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
              Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lock confirm dialog */}
      <AlertDialog open={confirmLock} onOpenChange={setConfirmLock}>
        <AlertDialogContent data-testid="lock-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Periode definitief maken?</AlertDialogTitle>
            <AlertDialogDescription>
              Hiermee wordt week <strong>{week}/{year}</strong> voor <strong>{selectedSlug}</strong> afgesloten.
              Wijzigingen aan uren of correcties zijn daarna niet meer mogelijk (alleen owner/admin kan ontgrendelen).
              <br /><br />
              Totaal uitbetaling: <strong>{fmtEur(totals.total_payout_eur || 0)}</strong> voor {totals.employees || 0} medewerker(s).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lock-cancel">Annuleren</AlertDialogCancel>
            <AlertDialogAction
              data-testid="lock-confirm"
              onClick={doLock}
              className="bg-brand-navy hover:bg-brand-navy/90 text-white"
            >
              Definitief maken
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
